import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { FIREBASE_CONFIG } from "./configConstants";
import NotificationToast from "../components/notificationToast/NotificationToast";
import { toast } from "react-toastify";
import { getAllNotificationsAPI, supportAPI } from "./api";
import store from "../store";
import { setNotificationCount } from "../store/slices/countState";
import { setMessageHistory, setTicketId, setTicketStatus, setTitle } from "../store/slices/supportTicketState";

export const firebaseConfig = {
    apiKey: FIREBASE_CONFIG.API_KEY,
    authDomain: FIREBASE_CONFIG.AUTH_DOMAIN,
    projectId: FIREBASE_CONFIG.PROJECT_ID,
    storageBucket: FIREBASE_CONFIG.STORAGE_BUCKET,
    messagingSenderId: FIREBASE_CONFIG.MESSAGING_SENDER_ID,
    appId: FIREBASE_CONFIG.APP_ID,
  };

export const FIREBASE_VAPID_KEY = FIREBASE_CONFIG.VAPID_KEY;

const app = initializeApp(firebaseConfig);

// Check if Firebase messaging is supported in this browser
let messaging = null;
let isMessagingSupported = false;

const initializeMessaging = async () => {
  try {
    const supported = await isSupported();
    if (supported) {
      messaging = getMessaging(app);
      isMessagingSupported = true;
      console.log('Firebase messaging is supported in this browser');
    } else {
      console.log('Firebase messaging is not supported in this browser');
      isMessagingSupported = false;
    }
  } catch (error) {
    console.error('Error checking Firebase messaging support:', error);
    isMessagingSupported = false;
  }
};

// Register service worker
export const registerServiceWorker = async () => {
  if (!isMessagingSupported) {
    console.log('Messaging not supported, skipping service worker registration');
    return null;
  }

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('Service Worker registered successfully:', registration);
      
      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New service worker installed, reloading page...');
              window.location.reload();
            }
          });
        }
      });
      
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  } else {
    console.log('Service Worker not supported');
    return null;
  }
};

export const requestForToken = async () => {
  if (!isMessagingSupported || !messaging) {
    console.log('Firebase messaging is not supported in this browser');
    return null;
  }

  // First register the service worker
  const registration = await registerServiceWorker();
  console.log("Registration",registration)
  
  if (!registration) {
    console.log('Cannot get token without service worker registration');
    return null;
  }

  return getToken(messaging, { vapidKey: FIREBASE_VAPID_KEY })
    .then((currentToken) => {
      if (currentToken) {
        console.log('FCM Token:', currentToken);
        return currentToken;
      } else {
        console.log(
          "No registration token available. Request permission to generate one."
        );
        return null;
      }
    })
    .catch((err) => {
      console.log("An error occurred while retrieving token:", err);
      return null;
    });
};

// Function to update notification count
const updateNotificationCount = async () => {
  try {
    const response = await getAllNotificationsAPI();
    
    if (response?.status === 200 && response.data) {
      const unreadCount = response.data.filter(notification => notification?.read === 0).length;
      store.dispatch(setNotificationCount(unreadCount));
    }
  } catch (error) {
    console.log('Error fetching notification count:', error);
  }
};

// Handle foreground messages
const setupMessageListener = async () => {
  if (!isMessagingSupported || !messaging) {
    console.log('Firebase messaging is not supported, skipping message listener setup');
    return;
  }

  try {
    onMessage(messaging, async (payload) => {
      console.log('Message received in foreground:', payload);
      
      if (payload?.notification) {
        // Check if user is currently in a chat with the notification sender
        const currentState = store.getState();
        const chatUser = currentState?.chatState?.chatUser;
        
        // Parse user data from notification payload
        const userData = typeof payload?.data?.user === 'string'
          ? JSON.parse(payload?.data?.user)
          : payload?.data?.user;

        // Check if user is currently on chat conversation screen
        const currentPath = window.location.pathname;
        const isOnChatConversation = currentPath == '/chat-conversation';
        
        if (chatUser && userData?._id && chatUser?._id == userData?._id && isOnChatConversation) {
          console.log('User is currently in chat with notification sender and on chat conversation screen, not showing notification');
          return;
        }

        // Check if user is currently on support chat screen
        const isOnSupportChat = currentPath == '/support-chat';
        const currentTicketId = currentState?.supportTicketState?.ticketId;
        if(payload?.data?.ticket_id && isOnSupportChat && currentTicketId == payload?.data?.ticket_id){
          const reqData = {
            ticket_id: payload?.data?.ticket_id,
          };
          const response = await supportAPI.getChats(reqData);
          if (response?.status === 200) {
            store.dispatch(setTicketId(response?.data?._id));
            store.dispatch(setTitle(response?.data?.subject));
            store.dispatch(setTicketStatus(response?.data?.status));
            store.dispatch(setMessageHistory(response?.data?.chat || []));
          } 
          return;
        }
        
        // Update notification count when new notification is received
        updateNotificationCount();
        
        toast(<NotificationToast payload={payload} />, {
          autoClose: 5000,
          position: "top-right",
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          closeButton: false,
        });
      }
    });
  } catch (error) {
    console.error('Error setting up message listener:', error);
  }
};

// Setup message listener after messaging is initialized
initializeMessaging().then(() => {
  setupMessageListener();
});

// Export function to check if messaging is supported
export const isFirebaseMessagingSupported = () => {
  return isMessagingSupported;
};

// Export function to get messaging instance (if supported)
export const getMessagingInstance = () => {
  return messaging;
};